import React, { useState } from 'react';
import { Box, Paper, Typography, Chip, Tooltip } from '@mui/material';
import { Calendar, dateFnsLocalizer, Event, EventPropGetter } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { ko } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendar.css';
import { HistoryType } from '../../types/history.types';
import { DayEventsModal } from './DayEventsModal';
import { isSameDay } from 'date-fns';

// date-fns 로컬라이저 설정
const locales = { ko };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface CalendarEvent extends Event {
  resource?: {
    id: string;
    type: string;
    moldName: string;
  };
}

interface CalendarViewProps {
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
}

// 유형별 색상 매핑
const TYPE_COLORS: Record<HistoryType, string> = {
  inbound: '#0288d1',        // 밝은 파랑 - 입고
  outbound: '#0097a7',       // 청록 - 출고
  production: '#388e3c',     // 녹색 - 생산
  trial_production: '#ff6f00', // 진한 주황 - 시사출
  maintenance: '#f57c00',    // 주황 - 정비
  repair: '#d32f2f',         // 빨강 - 수리
  inspection: '#1976d2',     // 파랑 - 점검
  modification: '#7b1fa2',   // 보라 - 개조
  accident: '#c62828',       // 진한 빨강 - 사고
  disposal: '#757575',       // 회색 - 폐기
  other: '#616161',          // 진한 회색 - 기타
};

const TYPE_LABELS: Record<HistoryType, string> = {
  inbound: '입고',
  outbound: '출고',
  production: '생산',
  trial_production: '시사출',
  maintenance: '정비',
  repair: '수리',
  inspection: '점검',
  modification: '개조',
  accident: '사고',
  disposal: '폐기',
  other: '기타',
};

export const CalendarView: React.FC<CalendarViewProps> = ({
  events,
  onEventClick,
}) => {
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [view, setView] = useState<'month' | 'week' | 'day' | 'agenda'>('month');

  // 뷰 변경 핸들러
  const handleViewChange = (newView: any) => {
    if (['month', 'week', 'day', 'agenda'].includes(newView)) {
      setView(newView as 'month' | 'week' | 'day' | 'agenda');
    }
  };

  // 날짜 클릭 핸들러
  const handleSelectSlot = (slotInfo: { start: Date; end: Date }) => {
    setSelectedDate(slotInfo.start);
    setDayModalOpen(true);
  };

  // 선택된 날짜의 이벤트
  const dayEvents = selectedDate
    ? events
        .filter((event) => event.start && isSameDay(event.start as Date, selectedDate))
        .map((event) => ({
          id: event.resource?.id || '',
          title: String(event.title || ''),
          type: event.resource?.type as HistoryType,
          moldName: event.resource?.moldName || '',
        }))
    : [];

  // 이벤트 스타일 설정
  const eventStyleGetter: EventPropGetter<CalendarEvent> = (event) => {
    const type = event.resource?.type as HistoryType;
    const backgroundColor = TYPE_COLORS[type] || '#1976d2';
    
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        color: 'white',
        border: 'none',
      },
    };
  };

  // 이벤트 툴팁 래퍼
  const EventWrapper = ({ event, children }: any) => {
    const type = event.resource?.type as HistoryType;
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {TYPE_LABELS[type]}
            </Typography>
            <Typography variant="body2">{String(event.title)}</Typography>
          </Box>
        }
        arrow
      >
        <Typography 
          component="div" 
          sx={{ 
            width: '100%', 
            color: 'white !important',
            fontSize: '12px',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {String(event.title)}
        </Typography>
      </Tooltip>
    );
  };
  
  return (
    <Paper sx={{ p: 2, height: '750px', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">
          월별 이력
        </Typography>
        
        {/* 범례 */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {Object.entries(TYPE_LABELS).map(([type, label]) => (
            <Chip
              key={type}
              label={label}
              size="small"
              sx={{
                backgroundColor: TYPE_COLORS[type as HistoryType],
                color: 'white',
                fontSize: '10px',
                height: '20px',
              }}
            />
          ))}
        </Box>
      </Box>
      
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          culture="ko"
          views={['month', 'week', 'day', 'agenda']}
          view={view}
          onView={handleViewChange}
          defaultView="month"
          selectable
          popup
          eventPropGetter={eventStyleGetter}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={onEventClick}
          messages={{
            next: '다음',
            previous: '이전',
            today: '오늘',
            month: '월',
            week: '주',
            day: '일',
            agenda: '일정',
            date: '날짜',
            time: '시간',
            event: '이벤트',
            noEventsInRange: '이 기간에 이력이 없습니다.',
          }}
        />
      </Box>

      {/* 날짜별 이력 모달 */}
      <DayEventsModal
        open={dayModalOpen}
        date={selectedDate}
        events={dayEvents}
        onClose={() => setDayModalOpen(false)}
        onEventClick={(eventId) => {
          setDayModalOpen(false);
          const event = events.find((e) => e.resource?.id === eventId);
          if (event) onEventClick(event);
        }}
        typeColors={TYPE_COLORS}
        typeLabels={TYPE_LABELS}
      />
    </Paper>
  );
};
